import type { CreatureState } from '@/domain/creature/types';
import { hasTemperament, type TemperamentProfile } from './temperament';

export type ToyId = 'ball' | 'rope' | 'plush' | 'puzzle';
export type PlayStyle = 'active' | 'comfort' | 'focus';

export interface ToyDef {
  id: ToyId;
  style: PlayStyle;
  happinessDelta: number;
  weightDelta: number;
  disciplineDelta: number;
}

export const TOY_CATALOG: Record<ToyId, ToyDef> = {
  ball: { id: 'ball', style: 'active', happinessDelta: 20, weightDelta: -2, disciplineDelta: 0 },
  rope: { id: 'rope', style: 'active', happinessDelta: 18, weightDelta: -1, disciplineDelta: 2 },
  plush: { id: 'plush', style: 'comfort', happinessDelta: 25, weightDelta: 0, disciplineDelta: 0 },
  puzzle: { id: 'puzzle', style: 'focus', happinessDelta: 16, weightDelta: 0, disciplineDelta: 3 },
};

export function preferredToy(temperament: TemperamentProfile): ToyId {
  if (hasTemperament(temperament, 'playful') || hasTemperament(temperament, 'glutton')) return 'ball';
  if (hasTemperament(temperament, 'stubborn')) return 'rope';
  if (hasTemperament(temperament, 'needy') || hasTemperament(temperament, 'fragile')) return 'plush';
  return 'puzzle';
}

export function applyToy(
  state: CreatureState,
  toyId: ToyId,
  temperament: TemperamentProfile,
  consecutiveUses: number,
): CreatureState {
  const toy = TOY_CATALOG[toyId];
  const preferenceBonus = preferredToy(temperament) === toyId ? 3 : 0;
  const repetitionPenalty = consecutiveUses >= 2 ? 6 : 0;
  return {
    ...state,
    happiness: clamp(state.happiness + toy.happinessDelta + preferenceBonus - repetitionPenalty, 0, 100),
    weight: clamp(state.weight + toy.weightDelta, 1, 99),
    discipline: clamp(state.discipline + toy.disciplineDelta, 0, 100),
  };
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
